// Global Instructions Rule Applied!
//
// The stored PDFs of a statement run: one file, or many as one archive
// ("Download all" - zipped in the browser, as the capital call notices are).

import { getAccessToken } from '../../../core/auth/authService'
import { getSignedUrl } from '../../../core/services/filesService.ts'
import { createZip, uniqueArchiveName } from '../../../core/utils/zip.ts'
import type { ZipEntry } from '../../../core/utils/zip.ts'
import { store } from '../../../state_manager/store.ts'
import { saveBlob } from '../../document_reports/services/documentReportsService.ts'
import type { InvestorStatementRow } from '../../../state_manager/data_models/investor_statement_run.ts'

const PDF_MIME = 'application/pdf'

/** The stored PDF of a row, when it has one. */
export const storedFile = (row: InvestorStatementRow): { path: string; name: string } | null =>
  row.file_urls_investor_statements__file_url
    ? {
        path: row.file_urls_investor_statements__file_url,
        name: row.file_urls_investor_statements__file_name || `${row.investor_statement_types__label}.pdf`
      }
    : null

export async function fetchStoredFile(path: string): Promise<Blob> {
  const signed = await getSignedUrl(path, getAccessToken(store.getState().auth))
  const response = await fetch(signed.signed_url)
  if (!response.ok) throw new Error(`The stored file could not be downloaded (${response.status})`)
  return response.blob()
}

/** What a failed member of the archive is called in the toast. */
const rowName = (row: InvestorStatementRow): string =>
  `${row.prog__entity_name} - ${row.investors__name ?? row.investor_statement_types__short_label}`

/** A stored file to put in an archive, and what to call it in the toast
 *  when it cannot be fetched. */
export interface StoredFileEntry {
  path: string
  name: string
  label: string
}

/**
 * Save the given stored PDFs as one archive (a lone file is saved as it is
 * - no one wants to unzip one file). The files are fetched one after another
 * and held in memory: right for a fund, heavy for a run of every fund. The
 * archive is named `archiveStem`. Returns how many were saved and which
 * could not be fetched (by their labels).
 */
export async function downloadStoredFiles(
  files: StoredFileEntry[],
  archiveStem: string
): Promise<{ saved: number; failed: string[] }> {
  const entries: ZipEntry[] = []
  const failed: string[] = []
  const used = new Set<string>()
  for (const file of files) {
    try {
      const blob = await fetchStoredFile(file.path)
      entries.push({ name: uniqueArchiveName(file.name, used), bytes: new Uint8Array(await blob.arrayBuffer()) })
    } catch {
      failed.push(file.label)
    }
  }
  if (entries.length === 1 && failed.length === 0) {
    saveBlob(new Blob([entries[0].bytes], { type: PDF_MIME }), entries[0].name)
  } else if (entries.length > 0) {
    saveBlob(createZip(entries), `${archiveStem}.zip`)
  }
  return { saved: entries.length, failed }
}

/**
 * Save the stored PDFs of the given statements as one archive. The archive
 * is named `archiveStem`: the file names of a fund's statements share too
 * little for a name to be derived from them. Returns how many were saved
 * and which could not be fetched.
 */
export async function downloadStatements(
  rows: InvestorStatementRow[],
  archiveStem: string
): Promise<{ saved: number; failed: string[] }> {
  return downloadStoredFiles(
    rows.flatMap((row) => {
      const file = storedFile(row)
      return file ? [{ ...file, label: rowName(row) }] : []
    }),
    archiveStem
  )
}
